import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { subscriptionRepository } from "../repositories/subscription.repository";
import type { SubscriptionFeaturesResponse } from "../types/subscription.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { getSubscriptionLimitsEnabled } from "./useEnhancedSubscriptionFeatures";

/**
 * Query key helper for subscription features
 * Supports both terreiro and medium (personal) contexts
 */
export const subscriptionFeaturesQueryKey = (context?: {
  terreiroId?: string;
  mediumId?: string;
}) => {
  const id = context?.terreiroId ?? context?.mediumId ?? "no-context";
  const type = context?.terreiroId
    ? "terreiro"
    : context?.mediumId
      ? "medium"
      : "unknown";
  return ["subscription", type, id, "features"] as const;
};

interface UseSubscriptionFeaturesQueryProps {
  /** Override the terreiro ID (defaults to cached terreiro) */
  terreiroId?: string;
  /** Override the medium ID (defaults to cached medium) - for personal subscriptions */
  mediumId?: string;
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Hook to fetch subscription features for the current user/terreiro/medium
 *
 * Supports both terreiro and personal (medium) subscription contexts.
 * Automatically detects context from cached data if not explicitly provided.
 *
 * Returns the subscription tier and all available features with their limits.
 * Features include both numeric limits (maxEvents, maxMembers, etc.) and
 * boolean flags (advancedReports, customBranding, etc.).
 *
 * @example
 * ```tsx
 * // Auto-detect context
 * const { data, isLoading } = useSubscriptionFeaturesQuery();
 *
 * // Explicit terreiro context
 * const { data } = useSubscriptionFeaturesQuery({ terreiroId: 'terreiro-123' });
 *
 * // Explicit medium context (personal subscription)
 * const { data } = useSubscriptionFeaturesQuery({ mediumId: 'medium-456' });
 *
 * if (data?.features.advancedReports) {
 *   // Show advanced reports feature
 * }
 * ```
 */
export function useSubscriptionFeaturesQuery(
  props: UseSubscriptionFeaturesQueryProps = {},
) {
  const {
    terreiroId: overrideTerreiroId,
    mediumId: overrideMediumId,
    enabled = true,
  } = props;
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  const callerManaged =
    Object.prototype.hasOwnProperty.call(props, "terreiroId") ||
    Object.prototype.hasOwnProperty.call(props, "mediumId");

  // When caller provides explicit overrides, use ONLY those (don't mix with stale cached values).
  // This prevents race conditions when SubscriptionContext passes atomic context.
  const terreiroId = callerManaged ? overrideTerreiroId : cachedTerreiro?.id;
  const mediumId = callerManaged ? overrideMediumId : cachedMedium?.id;

  // Determine personal profile mode:
  // - Caller-managed: personal if no terreiroId provided
  // - Self-managed: personal if cachedTerreiro is explicitly null
  const isPersonalProfile = callerManaged
    ? !overrideTerreiroId
    : cachedTerreiro === null;

  // Prioritize terreiro context if both exist, BUT respect personal profile mode
  const hasTerreiro = !isPersonalProfile && !!terreiroId;
  const hasMedium = (isPersonalProfile || !hasTerreiro) && !!mediumId;

  // Build context object
  const context = hasTerreiro
    ? { terreiroId }
    : hasMedium
      ? { mediumId }
      : undefined;

  const shouldEnableQuery =
    enabled && getSubscriptionLimitsEnabled() && !!context;

  return useQuery<SubscriptionFeaturesResponse, Error>({
    queryKey: subscriptionFeaturesQueryKey(context),
    queryFn: () =>
      subscriptionRepository.getFeatures(
        hasTerreiro ? terreiroId : undefined,
        hasMedium ? mediumId : undefined,
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes (matches backend cache)
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: "always",
    enabled: shouldEnableQuery,
    retry: (failureCount, error) => {
      // Don't retry on 404 - API not implemented yet
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },

    retryDelay: (attemptIndex) => {
      return Math.min(1000 * 2 ** attemptIndex, 4000);
    },

    refetchOnWindowFocus: true,

    meta: {
      errorMessage: "Falha ao carregar recursos da assinatura",
    },
  });
}
